import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '@/components/apex/PageTransition';
import { Database, Zap, Lock, Eye, FileCheck, MessageCircle, ChevronDown, ChevronUp, Cpu, Clock, Layers } from 'lucide-react';

// ─── Architecture diagram data ───────────────────────────────────────────────
const DATA_SOURCES = [
  { name: 'CloudTrail', access: 'Read-only', derives: 'Every API call, identity action, change event' },
  { name: 'AWS Config', access: 'Read-only', derives: 'Resource configuration history and compliance state' },
  { name: 'ECR / Inspector', access: 'Read-only', derives: 'Container image metadata and vulnerability findings' },
  { name: 'IAM policies', access: 'Read-only', derives: 'Granted permissions and trust relationships' },
  { name: 'VPC Flow Logs', access: 'Read-only (optional)', derives: 'Network traversal evidence during exposure windows' },
  { name: 'Amazon Macie', access: 'Read-only (optional)', derives: 'Data sensitivity classifications on S3 resources' },
];

const LAMBDAS = [
  { name: 'apex-lens-api', role: 'Serves the APEX Lens frontend. Reads from DynamoDB hot layer only. Sub-100ms responses.' },
  { name: 'apex-lens-processor', role: 'Nightly batch job. Queries Athena over CloudTrail and Config Parquet data. Writes derived findings to DynamoDB.' },
  { name: 'apex-lens-iam', role: 'Nightly IAM snapshot. Reads all IAM policies and trust relationships. Writes graph to DynamoDB.' },
  { name: 'apex-lens-sync', role: 'Every 6 hours. Validates license with APEX control plane. Emits heartbeat telemetry. The only Lambda with outbound network access.' },
];

export default function HowItWorks() {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <PageTransition>
      <div className="space-y-8 pb-12" data-testid="how-it-works">

        {/* Header */}
        <div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Platform · How It Works</span>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Architecture & deployment model</h1>
          <p className="text-sm text-apex-neutral mt-1 max-w-2xl">
            APEX Lens deploys entirely inside your AWS environment. Your governance data never leaves your account.
            This page explains exactly what is deployed, what it reads, and what this demo represents.
          </p>
        </div>

        {/* Four-layer architecture — the philosophical thesis */}
        <Section
          icon={Layers}
          title="The four-layer architecture"
          subtitle="APEX Lens is a governance reasoning system. Understanding the four layers explains why it is categorically different from any existing security tool."
        >
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="four-layer-arch">
            {[
              {
                number: '1',
                title: 'Evidence Reality',
                color: '#0D9488',
                description: 'What objectively happened. CloudTrail, Config, VPC Flow Logs, ECR, Macie, Purview, Terraform, GitHub, ServiceNow. S3 Object Lock. SHA-256 per partition. Every record gets an Evidence ID at ingest. This layer cannot be altered without breaking the hash chain.',
                examples: ['127,847 CloudTrail events', 'S3 Object Lock (WORM)', 'SHA-256 at ingest'],
              },
              {
                number: '2',
                title: 'Governance Facts',
                color: '#0EA5E9',
                description: 'What APEX believes to be true and why. Bitemporal normalized records — every fact has two timestamps: when it occurred in the cloud, and when APEX learned and verified it. Proof status and provenance depth on every record.',
                examples: ['Bitemporal valid_from/valid_to', 'PROVEN / INFERRED / UNVERIFIABLE', 'Provenance depth score'],
              },
              {
                number: '3',
                title: 'Governance Reasoning',
                color: '#8B5CF6',
                description: 'What governance conclusions can be derived. This is the moat. Claims, intent comparison, constraint validation, contradiction modeling, causal chains, half-life decay, confidence degradation, proof obligations per framework. This layer is what no competitor currently builds.',
                examples: ['Claim-based proof objects', 'Governance invariants', 'Causal chains', 'Decision memory'],
              },
              {
                number: '4',
                title: 'Governance Consumption',
                color: '#EC4899',
                description: 'How governance truth is consumed. Dashboards, evidence packets, APIs, board reports, proof catalogs for auditors, machine-readable proof objects for insurers, regulator-facing proof exchange. Layer 4 is the interface to the product — not the product itself.',
                examples: ['Signed evidence packets', 'Claim API', 'Board narratives', 'Auditor proof catalogs'],
              },
            ].map((layer) => (
              <div
                key={layer.number}
                className="rounded-xl border border-slate-200 p-4"
                data-testid={`arch-layer-${layer.number}`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold mb-3"
                  style={{ backgroundColor: layer.color }}
                >
                  {layer.number}
                </div>
                <h4 className="text-sm font-bold text-apex-ink mb-1">{layer.title}</h4>
                <p className="text-[11px] text-apex-neutral leading-relaxed mb-3">{layer.description}</p>
                <div className="space-y-1">
                  {layer.examples.map((ex) => (
                    <div key={ex} className="flex items-center gap-1.5 text-[10px] font-mono" style={{ color: layer.color }}>
                      <span>›</span>
                      <span>{ex}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-apex-shell rounded-lg p-4 font-mono text-[11px] text-slate-300" data-testid="arch-thesis">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-2">Architecture thesis</div>
            <div className="text-slate-200 font-semibold leading-relaxed">
              "APEX Lens is a temporal governance reasoning engine that converts immutable cloud evidence into machine-verifiable governance claims with explicit proof semantics, invariant validation, causality, decision memory, and programmable evidence retrieval."
            </div>
            <div className="mt-2 text-slate-500 text-[10px]">
              Not a CSPM · Not a SIEM · Not a GRC overlay · A governance reasoning system
            </div>
          </div>
        </Section>

        {/* Architecture diagram */}
        <ArchitectureDiagram />

        {/* What gets deployed */}
        <Section
          icon={Zap}
          title="What gets deployed in your account"
          subtitle="One CloudFormation stack. Four Lambda functions. No agents. No persistent outbound connections."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {LAMBDAS.map((l, i) => (
              <motion.div
                key={l.name}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="bg-slate-50 rounded-lg border border-slate-200 p-4"
              >
                <div className="font-mono text-[11px] text-apex-lens font-semibold mb-1">{l.name}</div>
                <p className="text-[12px] text-apex-neutral leading-relaxed">{l.role}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: '8 DynamoDB tables', sub: 'Hot layer for dashboard queries' },
              { label: '1 Athena workgroup', sub: 'Cold queries over CloudTrail Parquet' },
              { label: '1 CloudFront distribution', sub: 'Serves the APEX Lens UI from your account' },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-lg border border-slate-200 p-3 text-center">
                <div className="text-sm font-semibold text-apex-ink">{item.label}</div>
                <div className="text-[11px] text-apex-neutral mt-0.5">{item.sub}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* What it reads */}
        <Section
          icon={Database}
          title="What APEX Lens reads"
          subtitle="All access is read-only. APEX Lens never writes to your source buckets or modifies any resource."
        >
          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">Source</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">Access</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">What APEX derives</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {DATA_SOURCES.map((s) => (
                  <tr key={s.name} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-3 font-mono text-apex-ink font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-apex-success font-mono">{s.access}</td>
                    <td className="px-4 py-3 text-apex-neutral leading-relaxed">{s.derives}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* What exits your account */}
        <Section
          icon={Lock}
          title="What leaves your account"
          subtitle="One outbound connection only. Everything else stays inside your AWS account boundary."
        >
          <div className="mt-4 bg-apex-shell rounded-lg p-5 text-slate-200">
            <div className="font-mono text-[11px] text-apex-lens mb-3 uppercase tracking-wider">
              The complete list of data sent to APEX servers
            </div>
            <div className="space-y-2 text-[12px]">
              {[
                'License validation status (valid / expired / revoked)',
                'Stack version number (e.g. v1.0.4)',
                'Account count — a number only, no account IDs or names',
                'Last processor run timestamp',
                'Health status (healthy / degraded / error)',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-apex-lens mt-0.5 shrink-0">›</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700 text-[11px] text-slate-400">
              No CloudTrail events. No findings. No resource names. No identities. No governance data of any kind.
            </div>
          </div>
        </Section>

        {/* Evidence integrity */}
        <Section
          icon={FileCheck}
          title="How evidence is generated and signed"
          subtitle="Every finding traces to an immutable source record. No finding exists without provenance."
        >
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                step: '1',
                title: 'Ingest',
                body: 'CloudTrail and Config data lands in your S3 buckets with Object Lock (WORM). APEX Lens never touches these buckets — it reads from your existing data lake.',
              },
              {
                step: '2',
                title: 'Compute',
                body: 'The processor Lambda runs Athena queries over your Parquet data nightly. Every query is deterministic — same input always produces the same output. No ML, no scoring.',
              },
              {
                step: '3',
                title: 'Sign',
                body: 'Each evidence record is SHA-256 hashed and cryptographically signed. The hash is stored separately from the source data. Tampering invalidates the chain.',
              },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="w-7 h-7 rounded-full bg-apex-lens/10 text-apex-lens text-xs font-bold flex items-center justify-center mb-3">
                  {item.step}
                </div>
                <h4 className="text-sm font-semibold text-apex-ink mb-1">{item.title}</h4>
                <p className="text-[11px] text-apex-neutral leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Demo honesty section */}
        <Section
          icon={Eye}
          title="What this demo represents"
          subtitle="We believe in showing exactly what is real and what is simulated."
        >
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-3">
                ✓ Real in this demo
              </div>
              <ul className="space-y-2 text-[12px] text-emerald-900">
                {[
                  'Platform architecture and component structure',
                  'API behavior — all data served via FastAPI endpoints',
                  'Design partner signup — posts to real MongoDB database',
                  'Evidence packet generation — produces real downloadable PDF',
                  'Board report sharing — generates real shareable token URLs',
                  'Evidence model, hash chain structure, and lineage logic',
                  'Demo Mode flow — 8-step guided navigation',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-500 shrink-0 mt-0.5">›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 mb-3">
                ◎ Simulated in this demo
              </div>
              <ul className="space-y-2 text-[12px] text-amber-900">
                {[
                  'Stratum Financial Group is a fictional organization',
                  'All findings, investigations, and metrics use seeded data',
                  'CloudTrail and Config data is not from a real AWS account',
                  'The nightly processor runs against seeded Parquet fixtures',
                  'Integration statuses reflect a planned production deployment',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-500 shrink-0 mt-0.5">◎</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-4 bg-slate-50 rounded-lg border border-slate-200 p-4 text-[12px] text-apex-neutral leading-relaxed">
            The APEX Lens concept was designed and validated with a United Nations agency whose CISO and executive leadership confirmed the governance questions it answers are real and not answered by existing tools. This demo simulates that validated architecture against fictional data so you can evaluate the product without connecting your own environment.
          </div>
        </Section>

        {/* Technical Architecture */}
        <Section
          icon={Cpu}
          title="Technical architecture"
          subtitle="How the evidence engine actually works — for architects and security engineers."
        >
          <div className="mt-4 space-y-4" data-testid="technical-architecture">
            {/* Data flow diagram */}
            <div className="bg-apex-shell rounded-xl p-5 font-mono text-[11px] text-slate-300 leading-loose">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Evidence computation pipeline</div>
              <div className="space-y-1">
                <div><span className="text-apex-lens">CloudTrail</span> · Parquet · S3 Object Lock → <span className="text-slate-400">Athena workgroup</span></div>
                <div><span className="text-apex-lens">AWS Config</span> · Configuration items · S3 → <span className="text-slate-400">Athena workgroup</span></div>
                <div><span className="text-apex-lens">ECR / Inspector</span> · Image metadata · API → <span className="text-slate-400">Lambda: apex-lens-iam</span></div>
                <div className="text-slate-600 pl-4">↓ nightly · 02:00 UTC · EventBridge Scheduler</div>
                <div><span className="text-slate-400">Lambda: apex-lens-processor</span> → deterministic SQL queries → DynamoDB (hot layer)</div>
                <div className="text-slate-600 pl-4">↓ sub-100ms · on UI request</div>
                <div><span className="text-slate-400">Lambda: apex-lens-api</span> → reads DynamoDB only → React frontend</div>
                <div className="text-slate-600 pl-4">↓ evidence export path</div>
                <div><span className="text-slate-400">Athena on-demand</span> → signed S3 URL → PDF / JSON evidence packet</div>
              </div>
            </div>

            {/* Deployment topology */}
            <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="deployment-topology">
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
                <span className="text-[10px] uppercase tracking-widest font-mono text-apex-neutral">Deployment topology</span>
              </div>
              <div className="p-5">
                <div className="flex flex-col lg:flex-row gap-4 items-stretch">

                  {/* Customer account */}
                  <div className="flex-1 border-2 border-dashed border-apex-lens/40 rounded-xl p-4 relative min-h-[200px]">
                    <div className="absolute -top-3 left-3 bg-white px-2 text-[10px] font-mono text-apex-lens uppercase tracking-wider">
                      Your AWS account
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {[
                        { label: 'CloudTrail', sub: 'S3 · Object Lock', icon: '📋' },
                        { label: 'AWS Config', sub: 'S3 · Object Lock', icon: '🔧' },
                        { label: 'Athena', sub: 'Query engine · cold', icon: '🔍' },
                        { label: 'apex-lens-api', sub: 'Lambda · reads DynamoDB', icon: '⚡' },
                        { label: 'apex-lens-processor', sub: 'Lambda · nightly batch', icon: '⚡' },
                        { label: 'DynamoDB', sub: '8 tables · hot layer', icon: '🗄️' },
                        { label: 'CloudFront', sub: 'Serves APEX Lens UI', icon: '🌐' },
                        { label: 'Cognito', sub: 'Authentication', icon: '🔐' },
                      ].map((item) => (
                        <div key={item.label} className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-center">
                          <div className="text-base">{item.icon}</div>
                          <div className="text-[10px] font-semibold text-apex-ink mt-0.5">{item.label}</div>
                          <div className="text-[9px] text-apex-neutral font-mono">{item.sub}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-center text-[10px] font-mono text-apex-lens">
                      ✓ All governance data stays here
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex lg:flex-col items-center justify-center gap-2 px-2">
                    <div className="hidden lg:block w-px flex-1 bg-gradient-to-b from-transparent via-slate-300 to-transparent" />
                    <div className="text-center">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-mono whitespace-nowrap">Only outbound</div>
                      <div className="text-[9px] font-mono text-slate-500 whitespace-nowrap">License heartbeat</div>
                      <div className="text-[8px] font-mono text-slate-400 whitespace-nowrap">every 6 hours</div>
                      <div className="text-[8px] font-mono text-slate-400 whitespace-nowrap">no governance data</div>
                    </div>
                    <div className="hidden lg:block w-px flex-1 bg-gradient-to-b from-transparent via-slate-300 to-transparent" />
                    <div className="lg:hidden w-8 h-px bg-slate-300" />
                  </div>

                  {/* APEX control plane */}
                  <div className="lg:w-44 border border-slate-300 rounded-xl p-4 relative bg-slate-50">
                    <div className="absolute -top-3 left-3 bg-white px-2 text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                      APEX control plane
                    </div>
                    <div className="space-y-2 mt-2">
                      {[
                        { label: 'License validation', icon: '🔑' },
                        { label: 'Version management', icon: '📦' },
                        { label: 'Subscription billing', icon: '💳' },
                        { label: 'APEX Portal', icon: '🖥️' },
                      ].map((item) => (
                        <div key={item.label} className="bg-white border border-slate-200 rounded-md px-2.5 py-1.5 flex items-center gap-2">
                          <span className="text-sm">{item.icon}</span>
                          <span className="text-[11px] text-apex-ink">{item.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-center text-[10px] font-mono text-slate-400">
                      No customer data
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Architecture decisions */}
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">Decision</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">What we chose</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-apex-neutral uppercase tracking-wider text-[10px]">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    { d: 'Compute model', c: 'Nightly Lambda batch + on-demand query', w: 'Balances freshness vs cost. Dashboard always fast from DynamoDB. Deep queries hit Athena only when needed.' },
                    { d: 'Storage layer', c: 'DynamoDB hot + S3 Parquet cold', w: 'Hot layer for sub-100ms UI. Cold layer for temporal reconstruction across 14+ months of history.' },
                    { d: 'Evidence integrity', c: 'SHA-256 hash at ingest + Object Lock', w: 'Every source partition hashed at write time. Hash stored separately. Verification at query time.' },
                    { d: 'Graph model', c: 'Materialized in DynamoDB, not a graph DB', w: 'Relationship queries over governance entities do not require graph traversal at scale. DynamoDB GSIs handle the join patterns APEX needs.' },
                    { d: 'Reconstruction model', c: 'Pre-computed nightly snapshots + live Athena fallback', w: 'Snapshots give sub-200ms reconstruction for common dates. Athena fallback answers arbitrary timestamp queries in under 30s.' },
                    { d: 'Multicloud', c: 'AWS first, schema-agnostic event model', w: 'The governance event schema is cloud-neutral. Azure Activity Logs and GCP Audit Logs map to the same entity model. Normalization layer abstracts provider differences.' },
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 font-semibold text-apex-ink text-[12px]">{row.d}</td>
                      <td className="px-4 py-3 font-mono text-apex-lens text-[11px]">{row.c}</td>
                      <td className="px-4 py-3 text-apex-neutral text-[12px] leading-relaxed">{row.w}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Honest limitations */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 mb-3">Honest limitations</div>
              <ul className="space-y-2 text-[12px] text-amber-900">
                {[
                  'Reconstruction accuracy depends on CloudTrail retention. Most organizations have 1–3 years. Events before that window cannot be reconstructed.',
                  'VPC Flow Logs coverage is optional. Network traversal evidence is only available where Flow Logs are enabled.',
                  'CloudTrail data events (S3 object-level) must be explicitly enabled. Many organizations only enable management events by default.',
                  'IAM policy simulation reflects policies at query time, not the exact policy version active at the historical timestamp. Policy version history is tracked separately via Config.',
                  'Macie classification is ingested as metadata. APEX Lens does not perform its own data classification.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-500 shrink-0 mt-0.5">◎</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        {/* Reconstruction engine explainer */}
        <Section
          icon={Clock}
          title="How temporal reconstruction works"
          subtitle="The engine that answers 'what was your governance posture on any given date' — explained."
        >
          <div className="mt-4 space-y-4" data-testid="reconstruction-explainer">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4" data-testid="reconstruction-path-snapshot">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-teal-700 mb-2">
                  Path 1 — Pre-computed snapshots (fast)
                </div>
                <p className="text-[12px] text-teal-900 leading-relaxed">
                  The nightly processor Lambda runs at 02:00 UTC and writes a complete governance snapshot to DynamoDB — control coverage state, identity permission footprints, container provenance, and exposure windows — for that day. Queries against recent dates hit this snapshot layer and return in under 200ms.
                </p>
                <div className="mt-2 font-mono text-[10px] text-teal-700">Response time: &lt; 200ms</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="reconstruction-path-athena">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2">
                  Path 2 — Live Athena reconstruction (complete)
                </div>
                <p className="text-[12px] text-slate-700 leading-relaxed">
                  For arbitrary historical dates or high-fidelity reconstruction, the api Lambda triggers an Athena query directly over the immutable CloudTrail and Config Parquet data in S3. Every resource configuration, IAM policy state, and control evaluation is reconstructed by replaying the event stream up to the requested timestamp.
                </p>
                <div className="mt-2 font-mono text-[10px] text-slate-500">Response time: 8 – 35 seconds depending on window</div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="reconstruction-coverage">
              <div className="text-[11px] font-semibold text-apex-ink mb-3">What reconstruction covers</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                {[
                  { item: 'IAM policies and trust relationships', note: 'Exact version at timestamp via Config history', covered: true },
                  { item: 'SCP and guardrail enforcement state', note: 'AWS Organizations policy history via CloudTrail', covered: true },
                  { item: 'Resource configuration state', note: 'AWS Config configuration items at timestamp', covered: true },
                  { item: 'Security group and network rules', note: 'Config + CloudTrail network change events', covered: true },
                  { item: 'Container image digest per running task', note: 'ECS task definition history via CloudTrail', covered: true },
                  { item: 'Network-level activity (Flow Logs)', note: 'Only where VPC Flow Logs are enabled', covered: 'partial' },
                  { item: 'S3 object-level data access', note: 'Only where CloudTrail data events enabled', covered: 'partial' },
                  { item: 'Application-level request content', note: 'Outside APEX Lens scope by design', covered: false },
                ].map((row) => (
                  <div key={row.item} className="flex items-start gap-2">
                    <span className={`shrink-0 mt-0.5 font-semibold ${
                      row.covered === true ? 'text-apex-success' :
                      row.covered === 'partial' ? 'text-amber-500' : 'text-slate-300'
                    }`}>
                      {row.covered === true ? '✓' : row.covered === 'partial' ? '◎' : '○'}
                    </span>
                    <div>
                      <div className="font-medium text-apex-ink">{row.item}</div>
                      <div className="text-[10px] text-apex-neutral font-mono mt-0.5">{row.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-apex-shell rounded-lg p-4 font-mono text-[11px] text-slate-300" data-testid="reconstruction-integrity">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-2">Reconstruction integrity guarantee</div>
              <div className="space-y-1.5">
                <div>1. Source Parquet partition identified for requested time window</div>
                <div>2. SHA-256 hash of partition verified against stored hash at ingest time</div>
                <div>3. Event stream replayed deterministically from partition start to requested timestamp</div>
                <div>4. Resulting governance state computed and signed as Evidence Record</div>
                <div>5. Evidence ID and hash returned with every reconstruction response</div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-700 text-slate-400">
                If any source partition hash fails verification, the reconstruction is rejected and flagged as a Missing Evidence finding. The system never silently returns unverified data.
              </div>
            </div>
          </div>
        </Section>

        {/* Architecture FAQ */}
        <Section icon={MessageCircle} title="Architecture FAQ" subtitle="Questions CISOs and security architects ask in the first meeting.">
          <div className="mt-4 space-y-2" data-testid="architecture-faq">
            {[
              { q: 'How long does deployment take?',
                a: 'One CloudFormation command. Stack deploys in approximately 8 minutes. First data ingestion begins immediately. First meaningful investigation appears within 3 days of CloudTrail connection.' },
              { q: 'What IAM permissions does APEX Lens require?',
                a: 'Read-only access to CloudTrail S3 buckets, AWS Config delivery channel, ECR image metadata, IAM policy documents, and optionally VPC Flow Logs and Macie findings. No write permissions. No cross-account write access. A sample IAM policy is available in the deployment guide.' },
              { q: 'Does it require AWS Organizations or management account access?',
                a: 'APEX Lens deploys into your AWS management account to read cross-account CloudTrail via Organizations. A read-only cross-account role is established per member account. No changes are made to member account resources.' },
              { q: 'Is it agentless?',
                a: 'Yes. No agents, no sensors, no persistent network connections. Four Lambda functions run on a nightly schedule and on-demand for UI queries. The only outbound connection is the license heartbeat every 6 hours.' },
              { q: 'What does it cost to run?',
                a: 'AWS infrastructure cost is approximately $150–400/month for a 47-account organization, dominated by Athena query cost. Larger organizations with longer retention windows cost proportionally more. Detailed cost estimates are available on request.' },
              { q: 'How far back does it go?',
                a: 'As far back as your CloudTrail retention. Most organizations have 1–3 years. APEX Lens can query any period where CloudTrail Parquet data exists in your S3 bucket. Evidence Reconstruction works on any date in that window.' },
              { q: 'Does it work with Azure or GCP?',
                a: 'AWS is the current deployment target. Azure Activity Logs and Azure Resource Graph integration is on the roadmap. The evidence model is designed to be cloud-agnostic — the same governance questions apply identically across providers.' },
              { q: 'What happens to our data if we cancel?',
                a: 'Nothing. Your data never left your AWS account. The APEX Lens CloudFormation stack can be deleted in one command. Your CloudTrail and Config data remains in your S3 buckets exactly as before.' },
            ].map((item, i) => (
              <div key={i} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-panel-${i}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-apex-ink">{item.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 text-apex-neutral shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-apex-neutral shrink-0" />}
                </button>
                {openFaq === i && (
                  <div id={`faq-panel-${i}`} role="region" className="px-4 pb-4 text-[13px] text-apex-neutral leading-relaxed border-t border-slate-100 pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

      </div>
    </PageTransition>
  );
}

// ─── Architecture Diagram (SVG) ──────────────────────────────────────────────
function ArchitectureDiagram() {
  return (
    <div className="bg-apex-shell rounded-xl border border-slate-700 p-6" data-testid="architecture-diagram">
      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400 mb-5">Deployment architecture</div>

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">

        {/* Customer account box */}
        <div className="flex-1 border-2 border-dashed border-teal-500/40 rounded-xl p-4 relative">
          <div className="absolute -top-3 left-4 bg-apex-shell px-2 text-[10px] font-mono text-apex-lens uppercase tracking-wider">
            Your AWS account
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { label: 'CloudTrail', sub: 'S3 + Object Lock', icon: '📋' },
              { label: 'AWS Config', sub: 'S3 + Object Lock', icon: '🔧' },
              { label: 'APEX Lambdas', sub: '4 functions · read-only', icon: '⚡' },
              { label: 'DynamoDB', sub: '8 tables · findings', icon: '🗄️' },
              { label: 'Athena', sub: 'Cold query engine', icon: '🔍' },
              { label: 'CloudFront', sub: 'Serves APEX Lens UI', icon: '🌐' },
            ].map((item) => (
              <div key={item.label} className="bg-slate-800 rounded-lg p-2.5 text-center">
                <div className="text-base mb-1">{item.icon}</div>
                <div className="text-[11px] font-semibold text-slate-200">{item.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{item.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-center text-[10px] text-slate-500 font-mono">
            All data stays here
          </div>
        </div>

        {/* Arrow */}
        <div className="flex lg:flex-col items-center justify-center gap-2 py-2">
          <div className="flex-1 lg:flex-none h-px lg:h-16 lg:w-px bg-slate-600 lg:mx-auto" />
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-mono whitespace-nowrap">
              License heartbeat only
            </div>
            <div className="text-[9px] text-slate-500 font-mono whitespace-nowrap">
              No governance data
            </div>
          </div>
          <div className="flex-1 lg:flex-none h-px lg:h-16 lg:w-px bg-slate-600 lg:mx-auto" />
        </div>

        {/* APEX control plane */}
        <div className="lg:w-48 border border-slate-700 rounded-xl p-4 relative bg-slate-800/60">
          <div className="absolute -top-3 left-4 bg-apex-shell px-2 text-[10px] font-mono text-slate-400 uppercase tracking-wider">
            APEX control plane
          </div>
          <div className="space-y-2 mt-2">
            {[
              { label: 'License validation', icon: '🔑' },
              { label: 'Version management', icon: '📦' },
              { label: 'Subscription', icon: '💳' },
              { label: 'APEX Portal', icon: '🖥️' },
            ].map((item) => (
              <div key={item.label} className="bg-slate-700/60 rounded-md px-3 py-2 flex items-center gap-2">
                <span className="text-sm">{item.icon}</span>
                <span className="text-[11px] text-slate-300">{item.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-center text-[10px] text-slate-500 font-mono">
            No customer data
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl border border-slate-200 p-5 lg:p-6"
    >
      <div className="flex items-start gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg bg-apex-lens/10 text-apex-lens flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-base font-bold text-apex-ink">{title}</h2>
          <p className="text-[12px] text-apex-neutral mt-0.5 leading-relaxed">{subtitle}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );
}
